import { useQueryClient } from '@tanstack/react-query';
import { useState, type ReactNode } from 'react';
import { useNavigate } from 'react-router-dom';

import { AppShell } from '@/components/layout/AppShell';
import {
  Alert,
  Button,
  ButtonLink,
  Card,
  DataRow,
  EmptyState,
  Skeleton,
  SkeletonGroup,
} from '@/components/ui';
import { documentsFromQuote, withoutDocument } from '@/features/pricing/basket';
import { useAnnouncer } from '@/lib/a11y';
import { useMeta } from '@/lib/api/meta';
import { useIsDesktop } from '@/lib/hooks/useMediaQuery';
import { queryKeys, useApiMutation } from '@/lib/api/queryClient';
import { createQuote, discardBasket, useCurrentBasket } from '@/lib/api/quotes';
import type { QuoteItemOut, QuoteOut } from '@/lib/api/types';
import { formatPages, formatPaise } from '@/lib/format';
import { paths } from '@/routes/paths';
import { stepUrl } from '@/routes/order/steps';

/**
 * /cart — every document in the basket, editable before anything is ordered.
 *
 * Works signed in or not. The server owns the basket: by user when there is
 * one, by browser-session cookie otherwise (see `find_current_basket`). So
 * nothing here needs an account, and the basket a visitor builds anonymously
 * is the same one they check out with after signing in.
 *
 * This is the one screen in the app that does NOT read a quote id from the
 * URL. It cannot: the header icon that leads here exists on every page,
 * including pages that know nothing about an order. That is why the server
 * answers "what is this person's basket?" directly.
 *
 * REMOVING RE-CREATES THE QUOTE rather than deleting a row — the rule the
 * whole flow runs on (see features/pricing/basket.ts). A quote is priced
 * server-side from scratch, and taking a document out changes the weight and
 * therefore the delivery charge, so re-pricing the whole basket is what has to
 * happen anyway. The new quote becomes the current basket by being the newest.
 *
 * EDITING IS A LINK into the options step for that file, not a second editor
 * here. That step already prices every preset and every binding against the
 * rest of the basket; a cut-down copy on this page would be a second thing to
 * keep correct, and it would be the one that is wrong.
 */
export function CartPage() {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { announce } = useAnnouncer();
  const basket = useCurrentBasket();
  const meta = useMeta();
  const isDesktop = useIsDesktop();

  const [confirmingEmpty, setConfirmingEmpty] = useState(false);

  const quote = basket.data ?? undefined;
  const items = quote?.items ?? [];

  const refreshBasket = async () => {
    await queryClient.invalidateQueries({ queryKey: queryKeys.currentBasket() });
  };

  const remove = useApiMutation<QuoteOut, QuoteItemOut>({
    mutationFn: (item) =>
      createQuote({
        documents: withoutDocument(documentsFromQuote(quote), item.upload_id),
        fulfilment_method: quote?.fulfilment_method ?? 'delivery',
        // Keep the PIN code so the delivery charge on the remaining documents
        // is the real one rather than "pending" until the address step.
        ...(quote?.pincode ? { pincode: quote.pincode } : {}),
      }),
    onSuccess: async (_next, item) => {
      await refreshBasket();
      announce(`${documentName(item)} removed. ${items.length - 1} left in your cart.`);
    },
  });

  const empty = useApiMutation<void, void>({
    mutationFn: () => discardBasket(),
    onSuccess: async () => {
      setConfirmingEmpty(false);
      await refreshBasket();
      announce('Your cart is empty.');
    },
  });

  if (basket.isPending) {
    return (
      <CartShell>
        <SkeletonGroup label="Loading your cart">
          <div className="flex flex-col gap-3">
            <Skeleton className="h-28 w-full" />
            <Skeleton className="h-28 w-full" />
          </div>
        </SkeletonGroup>
      </CartShell>
    );
  }

  if (basket.isError) {
    return (
      <CartShell>
        <Alert severity="danger" title="We could not load your cart">
          <p>{basket.error.message}</p>
          <div className="mt-3">
            <Button variant="secondary" onClick={() => void basket.refetch()}>
              Try again
            </Button>
          </div>
        </Alert>
      </CartShell>
    );
  }

  if (!quote || items.length === 0) {
    return (
      <CartShell>
        <EmptyState
          title="Your cart is empty"
          description="Upload a PDF and it turns up here. You can add several and change any of them before you pay."
          action={<ButtonLink to={paths.order.upload}>Upload a PDF</ButtonLink>}
        />
      </CartShell>
    );
  }

  // Unknown until meta lands, and "unknown" must not read as "you are full":
  // the server refuses an over-long basket anyway, so the limit here is only
  // ever a courtesy note.
  const maxDocuments = meta.data?.basket.max_documents ?? Number.POSITIVE_INFINITY;
  const canAddMore = items.length < maxDocuments;
  // Adding carries only the quote id: a fresh file starts at one copy with the
  // default binding, so the previous document's `?u=`, `?c=` and `?b=` must not
  // travel with it.
  const addAnotherUrl = stepUrl('upload', { quote: quote.id });
  // Details is the next step in the flow proper. It needs an upload id in the
  // URL as well as the quote — any document in the basket satisfies that, so
  // the first one does.
  const continueUrl = stepUrl('details', { upload: items[0]?.upload_id, quote: quote.id });

  return (
    <CartShell
      subtitle={`${items.length === 1 ? '1 document' : `${items.length} documents`} · ${formatPaise(quote.total_paise)}`}
      action={
        <Button fullWidth onClick={() => navigate(continueUrl)}>
          Continue &middot; {formatPaise(quote.total_paise)}
        </Button>
      }
      // Rendered ONCE, in the rail on desktop and inline below it on touch —
      // see AppShell. Two copies would be two "Total" regions in the
      // accessibility tree, one of them hidden.
      aside={isDesktop ? <Totals quote={quote} /> : undefined}
    >
      <div className="flex flex-col gap-4">
        {remove.isError && remove.error ? (
          <Alert severity="danger" title="We could not remove that document" live>
            <p>{remove.error.message}</p>
          </Alert>
        ) : null}
        {empty.isError && empty.error ? (
          <Alert severity="danger" title="We could not empty your cart" live>
            <p>{empty.error.message}</p>
          </Alert>
        ) : null}

        {/* eslint-disable-next-line jsx-a11y/no-redundant-roles -- Safari/VoiceOver drops list semantics from a list-style:none list. */}
        <ul role="list" className="flex list-none flex-col gap-3 p-0">
          {items.map((item, index) => (
            <li key={item.upload_id}>
              <CartItem
                item={item}
                index={index}
                quoteId={quote.id}
                busy={remove.isPending}
                // Taking out the last document is emptying the cart, and the
                // server will not price a basket of nothing. Same act, so the
                // same confirmation rather than an error the customer has to
                // decipher.
                onRemove={() =>
                  items.length === 1 ? setConfirmingEmpty(true) : remove.mutate(item)
                }
              />
            </li>
          ))}
        </ul>

        <div className="flex flex-col gap-2">
          {canAddMore ? (
            <ButtonLink to={addAnotherUrl} variant="secondary" fullWidth>
              Add another document
            </ButtonLink>
          ) : (
            <p className="text-sm text-ink-subtle">
              {maxDocuments === 1 ? 'One document' : `${maxDocuments} documents`} is the most we can
              put in a single order. Anything more needs a second order.
            </p>
          )}

          {confirmingEmpty ? (
            <Alert severity="warning" title="Empty your cart?">
              <p>
                This clears all {items.length === 1 ? 'of it' : `${items.length} documents`}. Your
                files stay with us for a while, so you can put them back by uploading again.
              </p>
              <div className="mt-3 flex flex-wrap gap-2">
                <Button variant="danger" onClick={() => empty.mutate()} loading={empty.isPending}>
                  Yes, empty it
                </Button>
                <Button
                  variant="secondary"
                  onClick={() => setConfirmingEmpty(false)}
                  disabled={empty.isPending}
                >
                  Keep my cart
                </Button>
              </div>
            </Alert>
          ) : (
            <Button variant="ghost" fullWidth onClick={() => setConfirmingEmpty(true)}>
              Empty the cart
            </Button>
          )}
        </div>

        {/* On desktop the totals are in the rail beside the list; below `lg`
            there is no rail, so they go here, above the sticky action. */}
        {isDesktop ? null : <Totals quote={quote} />}
      </div>
    </CartShell>
  );
}

/** The shell, identical across the loading, error, empty and full states. */
function CartShell({
  children,
  subtitle,
  action,
  aside,
}: {
  children: ReactNode;
  subtitle?: string;
  action?: ReactNode;
  aside?: ReactNode;
}) {
  return (
    <AppShell
      title="Your cart"
      subtitle={subtitle}
      backTo={paths.home}
      backLabel="Home"
      action={action}
      aside={aside}
    >
      {children}
    </AppShell>
  );
}

function CartItem({
  item,
  index,
  quoteId,
  busy,
  onRemove,
}: {
  item: QuoteItemOut;
  index: number;
  quoteId: string;
  busy: boolean;
  onRemove: () => void;
}) {
  // Straight to this document's own options step, carrying the basket, so
  // "change the paper on the second file" lands on the screen that does that.
  //
  // `copies` goes in the URL because the options step reads it from there and
  // has no other source until Continue writes it back into a quote — arriving
  // without it would silently show a three-copy document as one copy.
  const editUrl = stepUrl('options', {
    upload: item.upload_id,
    quote: quoteId,
    copies: String(item.copies),
  });

  return (
    <Card title={documentName(item, index)}>
      <dl>
        <DataRow label="Pages" value={formatPages(item.page_count)} />
        <DataRow label="Copies" value={item.copies === 1 ? '1 copy' : `${item.copies} copies`} />
        <DataRow
          label="Printing and binding"
          value={formatPaise(item.print_cost_paise + item.binding_cost_paise)}
        />
      </dl>
      <div className="mt-3 flex flex-wrap gap-2 border-t border-line pt-3">
        <ButtonLink to={editUrl} variant="secondary">
          Edit
        </ButtonLink>
        <Button variant="ghost" onClick={onRemove} disabled={busy}>
          Remove
        </Button>
      </div>
    </Card>
  );
}

function Totals({ quote }: { quote: QuoteOut }) {
  return (
    <Card title="Total">
      <dl>
        <DataRow label="Printing" value={formatPaise(quote.print_cost_paise)} />
        {quote.binding_cost_paise > 0 ? (
          <DataRow label="Binding" value={formatPaise(quote.binding_cost_paise)} />
        ) : null}
        {quote.discount_paise > 0 ? (
          <DataRow label="Discount" value={`−${formatPaise(quote.discount_paise)}`} />
        ) : null}
        <DataRow
          label="Delivery"
          // Zero is genuinely free delivery; an unknown PIN code is not, and
          // showing "₹0.00" for it would be a promise we have not made.
          value={quote.pincode ? formatPaise(quote.shipping_paise) : 'Worked out at checkout'}
        />
        <DataRow label="GST" value={formatPaise(quote.tax_paise)} />
        <DataRow label="Total" value={formatPaise(quote.total_paise)} emphasis />
      </dl>
    </Card>
  );
}

/** The customer's own filename, or a position when the server has no name. */
function documentName(item: QuoteItemOut, index = item.position): string {
  return item.filename ?? `Document ${index + 1}`;
}
